import React, { useCallback, useEffect, useState } from "react";
import {
  FormDataConsumer,
  FunctionField,
  Labeled,
  ReferenceField,
  ShowProps,
  SimpleForm,
  TextField,
  useDataProvider,
  useNotify,
} from "react-admin";
import GoogleMapReact from "google-map-react";
import Marker from "./components/Marker";
import { BusSchedulePlanResources, getRouteService } from "utils/tools";
import {
  Typography,
  Grid,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Divider,
  Backdrop,
} from "@material-ui/core";
import SanitizedGrid from "components/SanitizedGrid";
import { GridinRow } from "utils/style";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import TabPanel from "./components/TabPanel";
import moment from "moment";
import { useWindowDimensions } from "utils/dimensionHook";
import { useStyles } from "./style";
import { useField, useFormState } from "react-final-form";
import { renderRoutePolyline, a11yProps, colors } from "./tools";
import LocationListComponent from "./components/LocationListComponent";
import SchoolLocationInDragable from "./components/SchoolLocationInDragable";
import LoadingComponent from "./components/LoadingComponent";
import CustomArrayChipField from "components/CustomArrayChipField";
import ClipboardButton from "./components/ClipboardButton";
import RouteList from "./components/RouteList";
import StudentList from "./components/StudentList";
import SwitchModeButton from "./components/SwitchModeButton";
import CustomDateField from "../../components/CustomDateField";
import DownloadRouteButton from "./components/DownloadRouteButton";
import { find, isEmpty, isEqual, last, orderBy, pullAllWith } from "lodash";
import ShowWrapper from "../../components/ShowWrapper";
import DriverWithPhoneField from "../assignments/components/DriverWithPhoneField";
import BackupOperatorChipField from "../assignments/components/BackupOperatorChipField";
import SubcontractorWithSubconIdentifierField from "../assignments/components/SubcontractorWithSubconIdentifierField";

const RoutePlanShow = (props: ShowProps) => {
  const [openRoutePanel, setOpenRoutePanel] = useState<boolean>(false);

  const classes = useStyles({ openRoutePanel });
  const provider = useDataProvider();
  const notify = useNotify();
  const { width } = useWindowDimensions();
  const { id } = props;
  const busSchedulePlanResource = BusSchedulePlanResources(Number(id));

  const [routes, setRoutes] = useState<any>([]);
  const [sortedRoutesByIndex, setSortedRoutesByIndex] = useState<any>([]);
  const [mapInstance, setMapInstance] = useState<any>(null);
  const [mapsLayoutInstance, setMapsLayout] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [polylines, setPolylines] = useState<any>([]);

  const [busScheduleId, setBusScheduleId] = useState<number>();
  const [busSchedule, setBusSchedule] = useState<IBusSchedule>();
  const [markers, setMarkers] = useState<any>([]);
  const [schools, setSchools] = useState([]);
  const [selectedMarkers, setSelectedMarkers] = useState<any>([]);
  const [isToSchool, setIsToSchool] = useState<boolean>(null);
  const [isMapView, setIsMapView] = useState<boolean>(true);
  const [keyRoute, setKeyRoute] = useState<number>();
  const [busSchedulePlan, setBusSchedulePlan] = useState<IBusSchedulePlans>();
  const [routeData, setRouteData] = useState<any>();

  useEffect(() => {
    provider.getOne("busSchedulePlans", { id: id }).then((result) => {
      setBusSchedulePlan(result.data as IBusSchedulePlans);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchRouteService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMarkers]);

  const fetchRouteService = async () => {
    let tempRoutes: any = [];

    // selectedMarkers.forEach(async (marker: any, index: number) => {
    for (let index = 0; selectedMarkers.length > index; index++) {
      if (index < selectedMarkers.length - 1) {
        const start = selectedMarkers[index];
        const end = selectedMarkers[index + 1];

        setIsLoading(true);

        try {
          const { routePolyline: route } = await getRouteService(start, end);

          // add index property for sorting the tempRoutes
          route.index = index;
          tempRoutes.push(route);

          // need to sort
          // because is async
          // and will make the routes not sequentially
          setSortedRoutesByIndex(orderBy(tempRoutes, ["index"], ["asc"]));
        } catch {
          // setToken(undefined);
          notify("Failed to load routeline", "warning");
          setIsLoading(false);
          break;
        }
      }
    }
  };

  useEffect(() => {
    let finalRoutes: any = [];

    // do check
    // make sure
    // the sortedRoutes is already complete filled with tempRoutes from func. getRouteService()
    if (sortedRoutesByIndex.length === selectedMarkers.length - 1) {
      sortedRoutesByIndex.forEach((sortedRoute: any) => {
        finalRoutes = [...finalRoutes, ...sortedRoute];
        setRoutes(finalRoutes);
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedRoutesByIndex]);

  useEffect(() => {
    if (mapInstance !== null && mapsLayoutInstance !== null) {
      renderPolylines(mapInstance, mapsLayoutInstance);
      setIsLoading(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapInstance, mapsLayoutInstance, routes]);

  const renderPolylines = (map: any, maps: any) => {
    const lineSymbol = {
      path: maps.SymbolPath.FORWARD_CLOSED_ARROW,
    };

    // if polyline is already draw
    // do reset the polyline
    if (openRoutePanel) {
      removePolyline();
    }

    let geodesicPolyline = new maps.Polyline({
      path: routes,
      geodesic: true,
      strokeColor: "#3B4E90",
      strokeOpacity: 1.0,
      strokeWeight: 2,
      icons: [
        {
          icon: lineSymbol,
          offset: "100%",
          repeat: "50px",
        },
      ],
    });

    setPolylines([...polylines, ...[geodesicPolyline]]);

    geodesicPolyline.setMap(map);
  };

  const handleApiLoaded = useCallback(
    (map: any, maps: any) => {
      // use map and maps objects

      setMapInstance(map);
      setMapsLayout(maps);
    },
    [setMapInstance, setMapsLayout],
  );

  const removePolyline = () => {
    if (polylines.length > 0) {
      polylines.forEach((polyline: any) => {
        polyline.setMap(null);
      });
    }
  };

  const addToSelectedMarker = (postalCode: number) => {
    let tempSelectedMarker = [];
    const findData = find(markers, ["postalCode", Number(postalCode)]);
    tempSelectedMarker.push(findData);

    if (selectedMarkers.length > 0) {
      if (isToSchool) {
        // find the last array
        // the last is school data
        const lastArray = last(selectedMarkers);

        // need to remove first
        // because for ToSchool wave
        // school element will always put on the last
        pullAllWith(selectedMarkers, [lastArray], isEqual);

        setSelectedMarkers([
          ...selectedMarkers,
          ...tempSelectedMarker,
          ...schools,
        ]);
      } else {
        setSelectedMarkers([...selectedMarkers, ...tempSelectedMarker]);
      }
    } else {
      if (isToSchool) {
        setSelectedMarkers([...tempSelectedMarker, ...schools]);
      } else {
        setSelectedMarkers([...schools, ...tempSelectedMarker]);
      }
    }
  };

  const handleOpenRoutePanel = (isEditMode?: boolean, index?: number) => {
    if (isEditMode) {
      setKeyRoute(index);
    }

    removePolyline();
    setOpenRoutePanel(true);
    setIsMapView(true);
  };

  const handleCloseRoutePanel = () => {
    setOpenRoutePanel(false);

    if (selectedMarkers.length > 0) {
      removePolyline();
      setSelectedMarkers([]);
    }
  };

  const [tabValue, setTabValue] = React.useState(0);
  const handleChangeTab = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
  };

  // set selected BusSchedule
  useEffect(() => {
    if (busScheduleId !== undefined && busScheduleId > 0) {
      provider.getOne("busSchedules", { id: busScheduleId }).then((result) => {
        setBusSchedule(result.data as IBusSchedule);

        provider
          .getOne("schools", { id: result.data.schoolId })
          .then((result) => {
            setSchools([result.data as ISchool]);
          });
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busScheduleId]);

  // set data for rendering markers
  useEffect(() => {
    if (busSchedule) {
      if (busSchedule.type === "to_school") {
        setIsToSchool(true);
      } else {
        setIsToSchool(false);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busSchedule]);

  const handleExistingMarkers = (value: any) => {
    if (isToSchool) {
      setSelectedMarkers([...value, ...schools]);
    }
    if (!isToSchool) {
      setSelectedMarkers([...schools, ...value]);
    }
  };

  useEffect(() => {
    if (busSchedulePlanResource !== undefined) {
      setBusScheduleId(busSchedulePlanResource.busScheduleId);

      let tmp: any = [];
      busSchedulePlanResource.routePlan?.routes?.forEach(
        (route: any, index: number) => {
          // While if the index more than or equal to 100, because we only have 100 hex colors
          while (index >= colors.length) {
            index -= colors.length;
          }

          let color = colors[index];
          route.routeLocations.forEach((location: any, key: number) => {
            location.id = key;
            location.isHaveRouteFromDatabase = true;
            location.color = color;
            location.name = route.name;
          });

          tmp = [...tmp, ...route.routeLocations];
        },
      );

      setMarkers(tmp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busSchedulePlanResource]);

  const FormFormatter = () => {
    const routePlanField = useField("routePlan");

    const { values } = useFormState();

    useEffect(() => {
      if (busSchedulePlan && isEmpty(values)) {
        const routePlan = busSchedulePlan.routePlan;
        routePlan?.routes.sort((a: any, b: any) =>
          a.name.localeCompare(b.name),
        );

        routePlanField.input.onChange(routePlan);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [busSchedulePlan]);

    return <></>;
  };

  const breadcrumb = [
    { link: "/busSchedulePlans", title: "Route Plans" },
    { link: `/busSchedulePlans/${props.id}/show`, title: props.id },
  ];

  return (
    <>
      <ShowWrapper
        breadcrumb={breadcrumb}
        customNameWithId="Routeplan"
        actions={false}
        isHaveSchoolFilter
        {...props}
      >
        <Grid container>
          <Grid item xs={width <= 1440 ? 5 : 4} className={classes.showPanel}>
            <SimpleForm toolbar={null}>
              <FormFormatter />
              <SanitizedGrid container spacing={2}>
                {openRoutePanel ? (
                  <>
                    <Grid item xs={12} style={GridinRow}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                          }}
                        >
                          <IconButton
                            aria-label="Back"
                            size="medium"
                            className={classes.sidePanelBackButton}
                            onClick={() => handleCloseRoutePanel()}
                          >
                            <KeyboardBackspaceIcon fontSize="inherit" />
                          </IconButton>
                          <Typography
                            variant="h6"
                            gutterBottom
                            className={classes.sidePanelTitle}
                          >
                            {(() => {
                              const findRoute =
                                busSchedulePlan.routePlan.routes[keyRoute];

                              return findRoute.name;
                            })()}
                          </Typography>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "end",
                          }}
                        >
                          <DownloadRouteButton
                            index={keyRoute}
                            isToSchool={isToSchool}
                            school={schools[0]}
                          />
                          <ClipboardButton
                            index={keyRoute}
                            isToSchool={isToSchool}
                            school={schools[0]}
                          />
                        </div>
                      </div>
                    </Grid>

                    <Grid item xs={12}>
                      <Tabs
                        value={tabValue}
                        onChange={handleChangeTab}
                        variant="fullWidth"
                        className={classes.sidePanelTabs}
                      >
                        <Tab label="Operations" {...a11yProps(0)} />
                        <Tab label="Students" {...a11yProps(1)} />
                        {/*<Tab label="Payment" {...a11yProps(2)} />*/}
                      </Tabs>
                      <TabPanel value={tabValue} index={0}>
                        <SanitizedGrid container spacing={2}>
                          <Grid item xs={6}>
                            <Labeled source="Start Time">
                              <Typography variant="subtitle1">
                                {moment(
                                  busSchedulePlan.routePlan.routes[keyRoute]
                                    .startTime,
                                  "HH:mm",
                                ).format("HH:mm")}
                              </Typography>
                            </Labeled>
                          </Grid>
                          <Grid item xs={6}>
                            <Labeled source="End Time">
                              <Typography variant="subtitle1">
                                {moment(
                                  busSchedulePlan.routePlan.routes[keyRoute]
                                    .endTime,
                                  "HH:mm",
                                ).format("HH:mm")}
                              </Typography>
                            </Labeled>
                          </Grid>

                          <Grid item xs={12}>
                            <Labeled source="Bus Bay Number">
                              <Typography variant="subtitle1">
                                {busSchedulePlan.routePlan.routes[keyRoute]
                                  .busBayNumber ?? "-"}
                              </Typography>
                            </Labeled>
                          </Grid>

                          <Grid item xs={12}>
                            <Labeled source="Driver">
                              <ReferenceField
                                source={`routePlan.routes[${keyRoute}].driverId`}
                                reference="drivers"
                                link={false}
                              >
                                <DriverWithPhoneField />
                              </ReferenceField>
                            </Labeled>
                          </Grid>

                          <Grid item xs={12}>
                            <Labeled source="Backup Drivers">
                              <BackupOperatorChipField
                                source={`routePlan.routes[${keyRoute}].backupDrivers`}
                                chipSource="fullName"
                              />
                            </Labeled>
                          </Grid>

                          <Grid item xs={12}>
                            <Labeled source="Bus Assistant">
                              <ReferenceField
                                source={`routePlan.routes[${keyRoute}].busAssistantId`}
                                reference="busAssistants"
                                link={false}
                              >
                                <DriverWithPhoneField />
                              </ReferenceField>
                            </Labeled>
                          </Grid>

                          <Grid item xs={12}>
                            <Labeled source="Backup Bus Assistants">
                              <BackupOperatorChipField
                                source={`routePlan.routes[${keyRoute}].backupBusAssistants`}
                                chipSource="fullName"
                              />
                            </Labeled>
                          </Grid>

                          <Grid item xs={12}>
                            <Labeled source="Vehicle">
                              <ReferenceField
                                source={`routePlan.routes[${keyRoute}].vehicleId`}
                                reference="vehicles"
                                link={false}
                              >
                                <FunctionField
                                  render={(record: any) =>
                                    `${record?.plateNumber} - ${
                                      record?.seatCapacity
                                        ? `${record?.seatCapacity} seat(s)`
                                        : `No seat info available`
                                    }`
                                  }
                                />
                              </ReferenceField>
                            </Labeled>
                          </Grid>

                          <Grid item xs={12}>
                            <Labeled source="Backup Vehicles">
                              <CustomArrayChipField
                                source={`routePlan.routes[${keyRoute}].backupVehicles`}
                                chipSource="plateNumber"
                                record={busSchedulePlan}
                              />
                            </Labeled>
                          </Grid>

                          <Grid item xs={12}>
                            <Labeled source="Subcontractor">
                              <ReferenceField
                                source={`routePlan.routes[${keyRoute}].assignedSubcontractor.subcontractorId`}
                                reference="subcontractors"
                                link={false}
                                emptyText="-"
                              >
                                <SubcontractorWithSubconIdentifierField />
                              </ReferenceField>
                            </Labeled>
                          </Grid>

                          <Grid item xs={6}>
                            <Labeled source="Subcontractor Start Date">
                              <CustomDateField
                                source={`routePlan.routes[${keyRoute}].assignedSubcontractor.startDate`}
                              />
                            </Labeled>
                          </Grid>
                          <Grid item xs={6}>
                            <Labeled source="Subcontractor End Date">
                              <CustomDateField
                                source={`routePlan.routes[${keyRoute}].assignedSubcontractor.endDate`}
                              />
                            </Labeled>
                          </Grid>
                          <Grid item xs={12}>
                            <Labeled source="Vehicle Size">
                              <TextField
                                source={`routePlan.routes[${keyRoute}].assignedSubcontractor.vehicleSize`}
                                emptyText="-"
                              />
                            </Labeled>
                          </Grid>
                        </SanitizedGrid>
                      </TabPanel>
                      <TabPanel value={tabValue} index={1}>
                        <SanitizedGrid container spacing={2}>
                          {!isToSchool && (
                            <Grid item xs={12}>
                              <SchoolLocationInDragable
                                schoolData={schools[0]}
                                waveType={"from_school"}
                                keyRoute={keyRoute}
                                routePlanData={busSchedulePlan.routePlan}
                              />
                            </Grid>
                          )}

                          <Grid item xs={12}>
                            <LocationListComponent
                              selectedMarkers={selectedMarkers}
                              waveType={
                                isToSchool ? "to_school" : "from_school"
                              }
                            />
                          </Grid>

                          {isToSchool && (
                            <Grid item xs={12}>
                              <SchoolLocationInDragable
                                schoolData={schools[0]}
                                waveType={"to_school"}
                                keyRoute={keyRoute}
                                routePlanData={busSchedulePlan.routePlan}
                              />
                            </Grid>
                          )}
                        </SanitizedGrid>
                      </TabPanel>
                      {/*<TabPayment tabValue={tabValue} keyRoute={keyRoute} />*/}
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={8} style={GridinRow}>
                      <Typography variant="h6" gutterBottom>
                        Route Plan
                      </Typography>
                      <Chip
                        label="Active"
                        color="primary"
                        className={classes.statusPlanActiveChip}
                      />
                    </Grid>

                    <Grid item xs={4}>
                      <SwitchModeButton
                        isMapView={isMapView}
                        setIsMapView={setIsMapView}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2">
                        Last Update At :{" "}
                        {(() => {
                          if (busSchedulePlan) {
                            const createdAt = moment.utc(
                              busSchedulePlan.createdAt,
                            );
                            const updateAt = moment.utc(
                              busSchedulePlan.updatedAt,
                            );
                            const diffDate = updateAt.diff(
                              createdAt,
                              "seconds",
                            );

                            if (diffDate < 1) {
                              return "-";
                            } else {
                              return updateAt.format("DD / MM / YYYY, HH:mm");
                            }
                          }
                        })()}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2">
                        Wave :{" "}
                        {busSchedule
                          ? `${busSchedule.name} - ${busSchedule.type}`
                          : ""}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2">
                        Arrive by :{" "}
                        {busSchedule
                          ? moment(busSchedule.arrivalTime, "HH:mm").format(
                              "HH:mm",
                            )
                          : ""}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2">
                        Days of the Weeks :{" "}
                        {busSchedule
                          ? `${(() => {
                              let days: string = "";

                              if (busSchedule.isMonday) {
                                days = days + " M,";
                              }
                              if (busSchedule.isTuesday) {
                                days = days + " T,";
                              }
                              if (busSchedule.isWednesday) {
                                days = days + " W,";
                              }
                              if (busSchedule.isThursday) {
                                days = days + " TH,";
                              }
                              if (busSchedule.isFriday) {
                                days = days + " F,";
                              }

                              return days.replace(/,(?=[^,]*$)/, "");
                            })()}`
                          : ""}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle2">Routes</Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <FormDataConsumer>
                        {({ formData, ...rest }) => {
                          return (
                            <>
                              {formData.routePlan?.routes && (
                                <RouteList
                                  routes={formData.routePlan?.routes}
                                  handleOpenRoutePanel={handleOpenRoutePanel}
                                  handleExistingMarkers={handleExistingMarkers}
                                  setRouteData={setRouteData}
                                />
                              )}
                            </>
                          );
                        }}
                      </FormDataConsumer>
                    </Grid>
                  </>
                )}
              </SanitizedGrid>
            </SimpleForm>
          </Grid>

          <Grid item xs={width <= 1440 ? 7 : 8}>
            <div className={classes.mapWrapper}>
              {isMapView ? (
                <GoogleMapReact
                  bootstrapURLKeys={{
                    key: "AIzaSyDYGvy_HvsUfvJ_7_d4PmOH201jIFNn-iM",
                  }}
                  defaultCenter={{
                    lat: 1.35896,
                    lng: 103.852309,
                  }}
                  defaultZoom={12}
                  yesIWantToUseGoogleMapApiInternals
                  onGoogleApiLoaded={({ map, maps }) =>
                    handleApiLoaded(map, maps)
                  }
                >
                  {markers.map((marker: any, key: number) => {
                    if (marker.students.length === 0) {
                      return <></>;
                    }

                    if (selectedMarkers.length > 0) {
                      const findSelectedMarker = find(selectedMarkers, [
                        "postalCode",
                        marker.postalCode,
                      ]);

                      if (!isEmpty(findSelectedMarker)) {
                        marker.isSelected = true;
                        marker.students = findSelectedMarker.students;
                      } else {
                        marker.isSelected = false;
                        marker.markerKey = `marker-${marker.postalCode}-${key}`;
                      }
                    } else {
                      marker.isSelected = !!marker.isHaveRouteFromDatabase;
                      marker.markerKey = `marker-${marker.postalCode}-${key}`;
                    }

                    marker.isSidePanelOpen = openRoutePanel;

                    return (
                      <Marker
                        lat={marker.lat}
                        lng={marker.lng}
                        key={`marker-${key}`}
                        isSelected={marker.isSelected ? true : false}
                        isSidePanelOpen={false}
                        resource={marker}
                        sendToParent={(postalCode: number) =>
                          addToSelectedMarker(postalCode)
                        }
                        color={marker.color}
                        name={marker.name}
                      />
                    );
                  })}

                  {schools.map((school: any) => (
                    <Marker
                      lat={school.lat}
                      lng={school.lng}
                      key={`school-marker-${school.postalCode}`}
                      resource={school}
                      isSchool
                    />
                  ))}
                </GoogleMapReact>
              ) : (
                <StudentList
                  routePlanId={busSchedulePlan.routePlan.id}
                  levels={busSchedule.levels}
                />
              )}
            </div>
          </Grid>
        </Grid>
      </ShowWrapper>

      <Backdrop className={classes.backdropLoading} open={isLoading}>
        {isLoading && <LoadingComponent />}
      </Backdrop>
    </>
  );
};

export default RoutePlanShow;
